/**
 * @module useStorage
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-storage
 *
 * @remarks
 * Reactive storage composable with adapter pattern for localStorage, sessionStorage, or memory.
 *
 * Key features:
 * - Reactive refs that sync with storage
 * - localStorage, sessionStorage, and memory adapters
 * - TTL (time-to-live) for time-based cache expiration
 * - Custom serialization support
 * - SSR fallback to memory adapter
 * - Automatic cleanup on remove/clear
 *
 * Uses adapter pattern to abstract storage implementation details.
 *
 * @example
 * ```ts
 * import { useStorage } from '@vuetify/v0'
 *
 * const storage = useStorage()
 * const theme = storage.get('theme', 'light')
 * theme.value = 'dark' // persists automatically
 * ```
 */

// Composables
import { createPluginContext } from '#v0/composables/createPlugin'
import { useWindowEventListener } from '#v0/composables/useEventListener'
import { useLogger } from '#v0/composables/useLogger'

// Adapters
import { MemoryStorageAdapter } from '#v0/composables/useStorage/adapters'

// Globals
import { IN_BROWSER } from '#v0/constants/globals'

// Utilities
import { isArray, isNull, isNullOrUndefined, isObject } from '#v0/utilities'
import { ref, watch } from 'vue'

// Types
import type { StorageAdapter } from '#v0/composables/useStorage/adapters'
import type { Ref } from 'vue'

export interface StorageContext {
  /** Check if a key exists in storage */
  has: (key: string) => boolean
  /** Get a reactive ref for a storage key */
  get: <T>(key: string, defaultValue?: T) => Ref<T>
  /** Set a value for a storage key */
  set: <T>(key: string, value: T) => void
  /** Remove a key accessed through this storage instance. Keys persisted by a previous session but never read here are not removed. */
  remove: (key: string) => void
  /** Clear keys accessed through this storage instance. Keys persisted by a previous session but never read here are not removed. */
  clear: () => void
  /** Unprefixed keys currently cached by this instance (read or written this session). */
  keys: () => string[]
  /** Active storage backend (`localStorage` or a `StorageAdapter`). */
  adapter: StorageAdapter | Storage
}

export interface StorageOptions {
  /** The storage adapter to use. Defaults to localStorage in browser, MemoryStorageAdapter otherwise */
  adapter?: StorageAdapter
  /** The prefix to use for all storage keys. Defaults to 'v0:' */
  prefix?: string
  /** Custom serializer for reading and writing values. Defaults to JSON.parse/stringify */
  serializer?: {
    read: (value: string) => unknown
    write: (value: unknown) => string
  }
  /** Time-to-live in milliseconds. When set, expired entries return the default value on `get()` and `set()` automatically timestamps entries. */
  ttl?: number
}

export interface StorageContextOptions extends StorageOptions {
  /** The namespace for the storage context. Defaults to `v0:storage` */
  namespace?: string
}

export interface StoragePluginOptions extends StorageContextOptions {}

// Exports
export { MemoryStorageAdapter, StorageAdapter } from '#v0/composables/useStorage/adapters'

export type { StorageType } from '#v0/composables/useStorage/adapters'

/**
 * Resolves the browser's localStorage, or `null` when it is unavailable.
 *
 * @remarks
 * Reading `window.localStorage` throws a `SecurityError` when a browser policy
 * blocks site data (Chrome's "block all cookies", a sandboxed iframe without
 * `allow-same-origin`, Firefox's `dom.storage.enabled=false`), so the property
 * access itself has to be guarded.
 *
 * @returns The browser's localStorage, or `null` outside the browser or when it is refused.
 */
function localStorageOrNull (): Storage | null {
  if (!IN_BROWSER) return null

  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * Creates a new storage instance.
 *
 * @param options The options for the storage instance.
 * @template E The type of the storage context.
 * @returns A new storage instance.
 *
 * @see https://0.vuetifyjs.com/composables/plugins/use-storage
 *
 * @example
 * ```ts
 * import { createStorage } from '@vuetify/v0'
 *
 * const storage = createStorage()
 *
 * storage.set('username', 'MyUsername')
 *
 * const username = storage.get('username')
 *
 * console.log(username.value) // MyUsername
 *
 * storage.clear()
 * ```
 */
export function createStorage<
  E extends StorageContext,
> (options: StorageOptions = {}) {
  const local = localStorageOrNull()

  const {
    adapter = local ?? new MemoryStorageAdapter(),
    prefix = 'v0:',
    serializer = {
      read: JSON.parse,
      write: JSON.stringify,
    },
    ttl,
  } = options

  const logger = useLogger()

  const cache = new Map<string, Ref<unknown>>()
  const watchers = new Map<string, () => void>()

  function has (key: string) {
    const prefixedKey = `${prefix}${key}`
    return cache.has(prefixedKey) || !isNullOrUndefined(readStored(prefixedKey))
  }

  function readStored (prefixedKey: string) {
    let raw: string | null | undefined

    try {
      raw = adapter?.getItem(prefixedKey)
    } catch (error) {
      logger.error(`[v0:storage] Failed to read key "${prefixedKey}":`, error)
      return undefined
    }

    if (isNullOrUndefined(raw)) return undefined

    try {
      const parsed = serializer.read(raw)

      // Always unwrap v0's own envelope (identified by the __v0 discriminator)
      // so legacy values written under a previous `ttl` config keep reading
      // correctly after the option is removed.
      if (isObject(parsed) && '__v0' in parsed && '__v' in parsed) {
        const envelope = parsed as { __v0: number, __v: unknown, __t: number }
        if (ttl && Date.now() - envelope.__t > ttl) {
          adapter?.removeItem(prefixedKey)
          return undefined
        }
        return envelope.__v
      }

      return parsed
    } catch (error) {
      logger.error(`[v0:storage] Failed to parse stored value for key "${prefixedKey}":`, error)
      return undefined
    }
  }

  function writeStored (prefixedKey: string, value: unknown) {
    const wrapped = ttl ? { __v0: 1, __v: value, __t: Date.now() } : value

    try {
      adapter?.setItem(prefixedKey, serializer.write(wrapped))
    } catch (error) {
      logger.error(`[v0:storage] Failed to write key "${prefixedKey}":`, error)
    }
  }

  function get<T> (key: string, defaultValue?: T): Ref<T> {
    const prefixedKey = `${prefix}${key}`

    if (cache.has(prefixedKey)) {
      return cache.get(prefixedKey)! as Ref<T>
    }

    const stored = readStored(prefixedKey)
    const initialValue = stored ?? (isObject(defaultValue) || isArray(defaultValue)
      ? structuredClone(defaultValue)
      : defaultValue)
    const valueRef = ref<T>(initialValue as T)

    const stop = watch(valueRef, newValue => {
      if (isNullOrUndefined(newValue)) {
        adapter?.removeItem(prefixedKey)
      } else {
        writeStored(prefixedKey, newValue)
      }
    }, { deep: true })

    watchers.set(prefixedKey, stop)
    cache.set(prefixedKey, valueRef)

    return valueRef as Ref<T>
  }

  function set<T> (key: string, value: T) {
    const valueRef = get(key)
    valueRef.value = value
  }

  function remove (key: string) {
    const prefixedKey = `${prefix}${key}`
    const stop = watchers.get(prefixedKey)

    if (!stop) return

    stop()
    watchers.delete(prefixedKey)
    adapter?.removeItem(prefixedKey)
    cache.delete(prefixedKey)
  }

  function keys (): string[] {
    const out: string[] = []
    for (const key of cache.keys()) {
      out.push(key.startsWith(prefix) ? key.slice(prefix.length) : key)
    }
    return out
  }

  function clear () {
    if (watchers.size > 0) {
      for (const stop of watchers.values()) {
        stop()
      }
      watchers.clear()
    }

    if (cache.size > 0) {
      for (const key of cache.keys()) {
        adapter?.removeItem(key)
      }
      cache.clear()
    }
  }

  if (!isNull(local) && adapter === local) {
    useWindowEventListener('storage', (e: StorageEvent) => {
      if (!e.key?.startsWith(prefix)) return

      const valueRef = cache.get(e.key)
      if (!valueRef) return

      watchers.get(e.key)?.()

      valueRef.value = isNullOrUndefined(e.newValue) ? undefined : readStored(e.key)

      const stop = watch(valueRef, newValue => {
        if (isNullOrUndefined(newValue)) {
          adapter?.removeItem(e.key!)
        } else {
          writeStored(e.key!, newValue)
        }
      }, { deep: true })

      watchers.set(e.key, stop)
    })
  }

  return {
    has,
    get,
    set,
    remove,
    clear,
    keys,
    adapter,
  } as E
}

function createStorageFallback<
  E extends StorageContext = StorageContext,
> (): E {
  return createStorage<E>({ adapter: new MemoryStorageAdapter() })
}

export const [createStorageContext, createStoragePlugin, useStorage] =
  /* @__PURE__ */ createPluginContext<StorageContextOptions, StorageContext>(
    'v0:storage',
    options => createStorage(options),
    {
      fallback: () => createStorageFallback(),
      inspect: ctx => {
        const entries: Record<string, unknown> = {}
        for (const key of ctx.keys()) {
          entries[key] = ctx.get(key).value
        }
        const name = ctx.adapter === globalThis.localStorage
          ? 'localStorage'
          : (ctx.adapter === globalThis.sessionStorage
              ? 'sessionStorage'
              : ctx.adapter.constructor.name)
        return { adapter: name, entries }
      },
    },
  )
